import { useCallback, useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";

type FactorLike = {
  id: string;
  status?: string;
  factor_type?: string;
  factorType?: string;
  type?: string;
};

function normalizeToken(token: string): string {
  return token.replace(/\D/g, "").slice(0, 6);
}

function getFactorType(factor: FactorLike): string {
  return `${factor.factor_type ?? factor.factorType ?? factor.type ?? ""}`.toLowerCase();
}

function extractFactors(data: unknown): FactorLike[] {
  if (Array.isArray(data)) {
    return data as FactorLike[];
  }

  if (!data || typeof data !== "object") {
    return [];
  }

  const collections = Object.values(data).filter(Array.isArray) as FactorLike[][];
  const byId = new Map<string, FactorLike>();

  collections.flat().forEach((factor) => {
    if (factor?.id) {
      byId.set(factor.id, factor);
    }
  });

  return Array.from(byId.values());
}

async function listTotpFactors(): Promise<FactorLike[]> {
  const { data, error } = await supabase.auth.mfa.listFactors();

  if (error) {
    throw error;
  }

  return extractFactors(data).filter((factor) => getFactorType(factor) === "totp");
}

async function getVerifiedTotpFactor(): Promise<FactorLike | null> {
  const factors = await listTotpFactors();
  return factors.find((factor) => factor.status === "verified") ?? null;
}

function getReadableAuthError(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message) {
    return fallback;
  }

  const message = error.message.toLowerCase();

  if (message.includes("invalid login credentials")) {
    return "E-mail ou senha inválidos.";
  }

  if (message.includes("email not confirmed")) {
    return "O e-mail desta conta ainda não foi confirmado no Supabase.";
  }

  if (message.includes("challenge") || message.includes("mfa")) {
    return "Código do Authenticator inválido ou expirado.";
  }

  return fallback;
}

async function isAuthorizedAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("admin_users")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    return false;
  }

  return !!data?.user_id;
}

export type LoginResult =
  | { success: false }
  | { success: true; nextStep: "mfa-verify" };

export type ResolvedAdminStep = {
  step: "login" | "mfa-verify" | "authenticated";
};

export function useAdminAuth() {
  const { session, user, loading: sessionLoading } = useSessionContext();
  const [error, setError] = useState("");
  const [hasEnrolledMFA, setHasEnrolledMFA] = useState(false);

  const refreshMfaStatus = useCallback(async (currentUser?: User | null) => {
    if (!currentUser) {
      setHasEnrolledMFA(false);
      return false;
    }

    try {
      const isAdmin = await isAuthorizedAdmin(currentUser.id);
      if (!isAdmin) {
        setHasEnrolledMFA(false);
        return false;
      }

      const factor = await getVerifiedTotpFactor();
      const enrolled = !!factor;
      setHasEnrolledMFA(enrolled);
      return enrolled;
    } catch {
      setHasEnrolledMFA(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    void refreshMfaStatus(user);
  }, [refreshMfaStatus, sessionLoading, user]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setError("");

    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail || !password) {
      setError("Informe e-mail e senha.");
      return { success: false };
    }

    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (signInError || !data.user) {
      setError(getReadableAuthError(signInError, "Não foi possível entrar com essas credenciais."));
      return { success: false };
    }

    const isAdmin = await isAuthorizedAdmin(data.user.id);
    if (!isAdmin) {
      await supabase.auth.signOut();
      setError("Acesso não autorizado para esta área.");
      return { success: false };
    }

    try {
      const verifiedFactor = await getVerifiedTotpFactor();

      if (!verifiedFactor) {
        await supabase.auth.signOut();
        setHasEnrolledMFA(false);
        setError("A autenticação em duas etapas desta conta não está disponível. Use o procedimento seguro de recuperação do administrador.");
        return { success: false };
      }

      setHasEnrolledMFA(true);
      return { success: true, nextStep: "mfa-verify" };
    } catch (factorError) {
      await supabase.auth.signOut();
      setHasEnrolledMFA(false);
      setError(getReadableAuthError(factorError, "Não foi possível validar a configuração de segurança desta conta."));
      return { success: false };
    }
  }, []);

  const verifyLoginMFA = useCallback(async (token: string): Promise<boolean> => {
    setError("");

    const normalizedToken = normalizeToken(token);
    if (normalizedToken.length !== 6) {
      setError("Digite o código atual de 6 dígitos do Authenticator.");
      return false;
    }

    try {
      const factor = await getVerifiedTotpFactor();

      if (!factor) {
        await supabase.auth.signOut();
        setHasEnrolledMFA(false);
        setError("A autenticação em duas etapas desta conta não está disponível.");
        return false;
      }

      const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factor.id,
        code: normalizedToken,
      });

      if (verifyError) {
        setError(getReadableAuthError(verifyError, "Não foi possível validar o código do Authenticator."));
        return false;
      }

      setHasEnrolledMFA(true);
      return true;
    } catch (verifyError) {
      setError(getReadableAuthError(verifyError, "Não foi possível validar o código do Authenticator."));
      return false;
    }
  }, []);

  const resolveAdminStep = useCallback(async (): Promise<ResolvedAdminStep> => {
    if (!session || !user) {
      return { step: "login" };
    }

    const isAdmin = await isAuthorizedAdmin(user.id);
    if (!isAdmin) {
      await supabase.auth.signOut();
      setHasEnrolledMFA(false);
      return { step: "login" };
    }

    const { data, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (!aalError && data?.currentLevel === "aal2") {
      setHasEnrolledMFA(true);
      return { step: "authenticated" };
    }

    const verifiedFactor = await getVerifiedTotpFactor();
    if (!verifiedFactor) {
      await supabase.auth.signOut();
      setHasEnrolledMFA(false);
      return { step: "login" };
    }

    setHasEnrolledMFA(true);
    return { step: "mfa-verify" };
  }, [session, user]);

  const logout = useCallback(async () => {
    setError("");
    setHasEnrolledMFA(false);
    await supabase.auth.signOut();
  }, []);

  const cancelPendingAuth = useCallback(async () => {
    setError("");
    setHasEnrolledMFA(false);
    await supabase.auth.signOut();
  }, []);

  return {
    sessionLoading,
    error,
    setError,
    hasEnrolledMFA,
    login,
    verifyLoginMFA,
    resolveAdminStep,
    cancelPendingAuth,
    logout,
  };
}
